/**
 * @Author: Zero
 * @Date: 2025-11-11 09:55:49
 * @Explain: 测试玩家管理模块
 */
import type { Effect, Subscription } from 'umi';
// utils
import { getNonExpiredCache } from '../pages/testerManage/aiSummary/utils/aiSummaryCache';

export interface TesterState {
  /** 是否显示创建测试玩家管理模块 - 弹窗 */
  createTesterOptions: {
    isShow: boolean;
    confirmBtnText: string;
    header: string;
  };
  /** 是否内部跳转 */
  isInternalJump: boolean;
  /** 是否开启跳出拦截 */
  isLeaveFlag: boolean;
  /** 选择内容 - 语言tab选中项 */
  languageActive: string;
  /** 草稿缓存 */
  draftCache: {
    id: number;
    name: string;
    description: string;
    time: 0;
    saveList: any[];
    expandedList: any[];
  };
  createdTesterCache: {
    name: string;
    description: string;
  };
  /** 存储表单初始化的值-用于修改信息 **/
  initFormData: any;
  /** AI分析报表缓存数据 */
  cacheAiSummaryData: Record<string, string>;
}
export type ModelType = {
  namespace: string;
  state: TesterState;
  reducers: {
    setInitFormData: (state: TesterState, action: { payload: any }) => any;
    setCreateTesterOptions: (
      state: TesterState,
      action: { payload: any },
    ) => any;
    setInterceptRouteStatus: (
      state: TesterState,
      action: { payload: any },
    ) => any;
    setTabsActive: (state: TesterState, action: { payload: any }) => any;
    setDraftCache: (state: TesterState, action: { payload: any }) => any;
    resetDraftCache: (state: TesterState, action: { payload: any }) => any;
    setCreatedTesterCache: (
      state: TesterState,
      action: { payload: any },
    ) => any;
    /** 修改AI分析报表存储数据 */
    setCacheAiSummaryData: (
      state: TesterState,
      action: { payload: any },
    ) => any;
    /** 重置AI分析报表存储数据 */
    resetCacheAiSummaryData: (
      state: TesterState,
      action: { payload: any },
    ) => any;
  };
  effects: {
    checkAndFixCacheAiSummaryState: Effect; // 统一使用 dva 的 Effect 类型
  };
  subscriptions: {
    setup: Subscription;
  };
};

const TesterModel: ModelType = {
  namespace: 'TesterModel',
  state: {
    initFormData: undefined,
    // 是否显示创建活动 - 弹窗
    createTesterOptions: {
      isShow: false,
      confirmBtnText: '',
      header: '',
    },
    // 是否内部跳转
    isInternalJump: false,
    // 是否开启跳出拦截
    isLeaveFlag: false,
    // 选择内容 - 语言tab选中项
    languageActive: '',
    // 创建-测试人员-表单缓存

    // 草稿缓存
    draftCache: {
      id: 0,
      name: '',
      description: '',
      time: 0,
      saveList: [],
      expandedList: [],
    },
    // 创建测试人员-表单信息
    createdTesterCache: {
      name: '',
      description: '',
    },
    // AI分析报表缓存数据
    cacheAiSummaryData: {},
  },
  reducers: {
    // 修改-创建表单内容
    setInitFormData(state, action) {
      return {
        ...state,
        initFormData: action.payload,
      };
    },
    // 设置创建活动 - 弹窗配置
    setCreateTesterOptions(state, action) {
      return {
        ...state,
        createTesterOptions: action.payload,
      };
    },
    // 设置拦截路由状态
    setInterceptRouteStatus(state, action) {
      const { isInternalJump, isLeaveFlag } = action.payload;
      return {
        ...state,
        isInternalJump,
        isLeaveFlag,
      };
    },
    // 设置选择内容 - 语言tab选中项
    setTabsActive(state, action) {
      return {
        ...state,
        languageActive: action.payload,
      };
    },
    // 设置草稿缓存
    setDraftCache(state, action) {
      return {
        ...state,
        draftCache: action.payload,
      };
    },
    // 重置草稿缓存
    resetDraftCache(state) {
      return {
        ...state,
        draftCache: {
          id: 0,
          time: 0,
          saveList: [],
          expandedList: [],
        },
        languageActive: '',
      };
    },
    // 记录 创建测试人员-表单信息
    setCreatedTesterCache(state, action) {
      return {
        ...state,
        createdTesterCache: action.payload,
      };
    },
    // 修改AI分析报表存储数据
    setCacheAiSummaryData: (state, action) => {
      return {
        ...state,
        cacheAiSummaryData: {
          ...state.cacheAiSummaryData,
          ...action.payload,
        },
      };
    },
    // 修改AI分析报表存储数据
    resetCacheAiSummaryData: (state, action) => {
      return {
        ...state,
        cacheAiSummaryData: action.payload,
      };
    },
  },
  effects: {
    // 检查并修复AI分析报表存储数据状态的 effect
    *checkAndFixCacheAiSummaryState(action, { select, put }) {
      // 使用 select 获取当前的 state
      const { cacheAiSummaryData = {} } = yield select(
        (state) => state.TesterModel,
      );
      // 检查缓存是否需要更新 & 获取新的缓存数据
      const { newCache, cacheNeedUpdate } = getNonExpiredCache({
        cacheAiSummaryData,
      });
      // 检查内容是否需要更新
      if (cacheNeedUpdate) {
        // 调整 state 指定存储
        yield put({
          type: 'resetCacheAiSummaryData',
          payload: newCache,
        });
      }
    },
  },

  subscriptions: {
    setup({ dispatch, history }) {
      return history.listen(() => {
        // 当路由发生变化时，派发 action 去检查 state
        dispatch({
          type: 'checkAndFixCacheAiSummaryState',
        });
      });
    },
  },
};

export default TesterModel;
